import {useParams, Link, useNavigate} from 'react-router-dom';
import {useState, useEffect} from 'react';
import {Button} from '@common/ui/buttons/button';
import {useAuth} from '@common/auth/use-auth';
import {IconButton} from '@common/ui/buttons/icon-button';
import {CheckIcon} from '@common/icons/material/Check';
import {CloseIcon} from '@common/icons/material/Close';
import {EditIcon} from '@common/icons/material/Edit';
import {DeleteIcon} from '@common/icons/material/Delete';
import {toast} from '@common/ui/toast/toast';
import {DialogTrigger} from '@common/ui/overlays/dialog/dialog-trigger';
import {ConfirmationDialog} from '@common/ui/overlays/dialog/confirmation-dialog';

interface ShowSchedule {
  id: number;
  show_name: string;
  show_banner: string | null;
  show_video: string | null;
  show_description: string | null;
  show_date: string;
  show_time: string;
  show_location: string;
  show_ticket_link: string | null;
  user_id: number;
}

export function ArtistAgendaPanel() {
  const {artistId} = useParams();
  const {user} = useAuth();
  const navigate = useNavigate();
  const [shows, setShows] = useState<ShowSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [loggedInUser, setLoggedInUser] = useState<any>(null);

  useEffect(() => {
    fetchLoggedInUser();
    fetchShows();
  }, [artistId]);

  const fetchLoggedInUser = async () => {
    try {
      const response = await fetch('https://vibe.sintonizy.com/api/v1/users/me', {
        headers: {
          'Authorization': 'Bearer 1|vRqh3zQF5hC69jECxE1XI4o3iHED33cIQ9glQIlYe755d403',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logged in user');
      }

      const data = await response.json();
      console.log('Logged in user:', data);
      setLoggedInUser(data.user);
    } catch (error) {
      console.error('Error fetching logged in user:', error);
    }
  };

  const fetchShows = async () => {
    try {
      setError(null);
      const response = await fetch(`/api/agenda-shows.php?user_id=${artistId}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server response:', errorText);
        throw new Error('Failed to fetch shows');
      }

      const responseData = await response.json();
      console.log('Shows response:', responseData);
      
      if (responseData.status === 'error') {
        throw new Error(responseData.message || 'Failed to fetch shows');
      }

      setShows(responseData.data || []);
    } catch (error) {
      console.error('Error fetching shows:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch shows');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (showId: number) => {
    try {
      setDeleteLoading(showId);
      
      if (!loggedInUser) {
        throw new Error('User not authenticated');
      }

      console.log('Deleting show:', showId, 'User ID:', loggedInUser.id);

      const response = await fetch(`/api/agenda-shows.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `show_id=${showId}&user_id=${loggedInUser.id}`,
      });

      const data = await response.json();
      console.log('Delete response:', data);
      
      if (data.status === 'success') {
        toast.positive('Show deleted successfully');
        await fetchShows();
      } else {
        throw new Error(data.message || 'Failed to delete show');
      }
    } catch (error) {
      console.error('Error deleting show:', error);
      toast.danger(error instanceof Error ? error.message : 'Failed to delete show');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleEdit = (showId: number) => {
    navigate(`/agenda-shows/edit/${showId}`);
  };

  if (loading) {
    return <div className="p-24">Carregando Agenda...</div>;
  }

  if (error) {
    return <div className="p-24 text-danger">{error}</div>;
  }

  const isShowOwner = (show: ShowSchedule) => {
    return loggedInUser && show.user_id === loggedInUser.id;
  };

  return (
    <div className="container mx-auto p-20">
      {loggedInUser && loggedInUser.id === Number(artistId) && (
        <div className="mb-20">
          <Link to="/agenda-shows/create">
            <Button variant="flat" color="primary">
              Criar Nova Agenda
            </Button>
          </Link>
        </div>
      )}

      {shows.length === 0 ? (
        <div className="text-center p-24">Nenhum show agendado</div>
      ) : (
        <div className="grid gap-20 md:grid-cols-2 lg:grid-cols-3">
          {shows.map((show) => (
            <div key={show.id} className="border rounded-lg p-16 relative">
              {show.show_banner && (
                <img
                  src={show.show_banner}
                  alt={show.show_name}
                  className="w-full h-72 object-cover rounded-lg mb-16"
                />
              )}
              {show.show_video && (
                <video
                  src={show.show_video}
                  controls
                  className="w-full h-72 object-cover rounded-lg mb-16"
                >
                  Your browser does not support the video tag.
                </video>
              )}
              <h3 className="text-xl font-semibold mb-8">{show.show_name}</h3>
              <p className="text-sm text-muted mb-8">{show.show_description}</p>
              <div className="text-sm mb-4">
                <strong>Data:</strong> {new Date(show.show_date).toLocaleDateString()}
              </div>
              <div className="text-sm mb-4">
                <strong>Hora:</strong> {show.show_time}
              </div>
              <div className="text-sm mb-4">
                <strong>Local:</strong> {show.show_location}
              </div>
              {show.show_ticket_link && (
                <a
                  href={show.show_ticket_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-8 text-primary hover:underline"
                >
                  Saiba Mais
                </a>
              )}
              
              {/* Botões de ação */}
              {isShowOwner(show) && (
                <div className="flex gap-8 mt-16 justify-end">
                {/*   <Button
                    variant="text"
                    color="primary"
                    onClick={() => handleEdit(show.id)}
                    startIcon={<EditIcon/>}
                  >
                    Editar
                  </Button>*/}
                  <Button
                    variant="text"
                    color="danger"
                    onClick={() => {
                      if (window.confirm('Tem certeza de que deseja excluir este agenda?')) {
                        handleDelete(show.id);
                      }
                    }}
                    disabled={deleteLoading === show.id}
                    startIcon={<DeleteIcon/>}
                  >
                    Deletar
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
